import { createTheme } from '@mui/material';

import { primaryGradient } from '~styles/colors/gradients/primary-gradient';
import { secondaryGradient } from '~styles/colors/gradients/secondary-gradient';

import {
  primary,
  neutral,
  fog,
  red,
  lime,
  violet,
  green,
  orange,
} from './colors';

export const colors = {
  primary: {
    ...primary, // for in-theme usage
    main: primary[100],
    contrastText: '#FFFFFF',
  },
  primaryTokens: primary, // for external usage

  neutral,
  fog,
  red,
  lime,
  violet,
  green,
  orange,

  // gradients: {
  //   1: `linear-gradient(45deg, ${violet['10']}, ${primary['25']})`,
  //   2: `linear-gradient(45deg, ${primary['100']}, ${green['100']})`
  // },

  gradients: {
    primaryGradient,
    secondaryGradient,
  },

  secondary: {
    main: '#2bc87a',
  },

  success: {
    main: '#73D13D',
  },
  error: {
    main: red['100'],
  },
  warning: {
    main: orange['100'],
  },
  info: {
    main: '#0063F7',
  },

  // dark0: '#262626',
  // dark1: '#343434',
  // dark2: '#595959',
  // dark3: '#8C8C8C',
  // dark4: '#D9D9D9',

  // light0: '#E4E4EB',
  // light1: '#EBEBF0',
  // light2: '#F2F2F5',
  // light3: '#FAFAFC',
  // light4: '#FFFFFF',
};

const baseBody = {
  fontFamily: ['Nunito Sans', 'sans-serif'].join(','),
  // fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Helvetica Neue,Arial,sans-serif!important',
  fontWeight: 400,
  // lineHeight: 1.5,
  letterSpacing: 0,
  // letterSpacing: '-0.3px',
  // color: fog['25']
};

const baseHeading = {
  fontFamily: ['Nunito Sans', 'sans-serif'].join(','),
  // fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Helvetica Neue,Arial,sans-serif!important',

  fontWeight: 600,
  lineHeight: 1,
  color: fog['25'],
  letterSpacing: '-0.7px',
};

const typography = {
  text: {
    'body-lg': {
      ...baseBody,
      fontSize: 18,
      // fontSize: 20,
    },
    'body-md': {
      ...baseBody,
      fontSize: 16,
    },
    'body-sm': {
      ...baseBody,
      fontSize: 14,
    },
    'body-caption': {
      ...baseBody,
      fontSize: 12,
    },

    h1: {
      ...baseHeading,
      fontWeight: 700,
      fontSize: 64,
    },
    h2: {
      ...baseHeading,
      fontWeight: 700,
      fontSize: 48,
    },
    h3: {
      ...baseHeading,
      fontWeight: 600,
      fontSize: 40,
    },
    h4: {
      ...baseHeading,
      fontWeight: 600,
      fontSize: 34,
    },
    h5: {
      ...baseHeading,
      fontWeight: 600,
      fontSize: 28,
    },
    h6: {
      ...baseHeading,
      fontWeight: 600,
      fontSize: 24,
    },
    h7: {
      ...baseHeading,
      fontWeight: 600,
      fontSize: 20,
    },
    h8: {
      ...baseHeading,
      fontWeight: 600,
      fontSize: 18,
    },
  },

  buttons: {
    medium: {
      fontSize: 16,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 24,
      padding: '10px 24px',
    },
    small: {
      fontSize: 16,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 24,
      padding: '6px 24px',
    },
    label: {
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 20,
      padding: '2px 12px',
    },
  },
};

const refactoredButtons: any = {
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      size: 'large',
    },
    styleOverrides: {
      root: {
        ...typography.text['body-md'],

        textTransform: 'none',
        borderRadius: 8,
        boxShadow: 'none',
        fontWeight: 600,
        transition:
          'background-color 0.2s, color 0.2s, box-shadow 0.2s, border-color 0.2s',
        lineHeight: '24px',
      },

      startIcon: {
        marginRight: 10,
        '& > *:nth-of-type(1)': {
          fontSize: 'unset',
        },
      },
      endIcon: {
        marginLeft: 10,
        '& > *:nth-of-type(1)': {
          fontSize: 'unset',
        },
      },
      // loadingIndicator: {
      //   color: 'inherit'
      // }
    },
    variants: [
      {
        props: { size: 'x-large' },
        style: {
          padding: '12px 24px',
        },
      },
      {
        props: { size: 'large' },
        style: {
          padding: '10px 24px',
        },
      },
      {
        props: { size: 'medium' },
        style: {
          padding: '6px 24px',
        },
      },
      {
        props: { size: 'small' },
        style: {
          padding: '8px 12px',
        },
      },
      {
        props: { size: 'x-small' },
        style: {
          padding: '8px 12px', // was 6px 8px
          ...typography.text['body-sm'],
          fontWeight: 600,
          lineHeight: '20px !important',
        },
      },

      /* overrides for outlined buttons */

      {
        props: { variant: 'outlined', size: 'x-large' },
        style: { padding: '11px 24px' },
      },
      {
        props: { variant: 'outlined', size: 'large' },
        style: { padding: '9px 24px' },
      },
      {
        props: { variant: 'outlined', size: 'medium' },
        style: { padding: '5px 24px' },
      },
      {
        props: { variant: 'outlined', size: 'small' },
        style: { padding: '7px 12px' },
      },
      {
        props: { variant: 'outlined', size: 'x-small' },
        style: { padding: '7px 12px' },
      },

      {
        props: {
          variant: 'contained',
          color: 'primary',
        },
        style: {
          backgroundColor: colors.primary[100],
          color: colors.neutral[100],

          '&:hover': {
            backgroundColor: colors.primary[125],
            boxShadow: 'none',
          },

          '&:disabled': {
            backgroundColor: colors.fog['80'],
            color: colors.neutral[100],
          },
        },
      },
      {
        props: {
          variant: 'text',
        },
        style: {
          '&:hover': {
            backgroundColor: colors.primary[25],
          },
        },
      },
      {
        props: {
          variant: 'link',
        },
        style: {
          padding: '10px 0',
          // ...typography.text['body-md'],
          textDecoration: 'underline',
          textDecorationThickness: '1px',
          textDecorationColor: colors.primary[100],

          color: colors.primary['100'],

          '&:hover': {
            textDecoration: 'underline',
            textDecorationThickness: '1px',
            color: colors.primary['125'],
          },
        },
      },
      {
        props: {
          variant: 'outlined',
          color: 'primary',
        },
        style: {
          color: colors.primary['100'],
          borderColor: colors.primary['100'],

          '&:hover': {
            color: colors.primary['125'],
            borderColor: colors.primary['125'],
            backgroundColor: colors.primary['10'],
          },
        },
      },
      {
        props: {
          variant: 'outlined',
          color: 'error',
        },
        style: {
          color: colors.red['100'],
          borderColor: colors.red['100'],

          '&:hover': {
            color: colors.red['125'],
            borderColor: colors.red['125'],
            backgroundColor: colors.red['10'],
          },
        },
      },
      {
        props: {
          variant: 'label',
        },
        style: {
          padding: '4px 12px',
          lineHeight: '16px',
          ...typography.text['body-sm'],
          fontWeight: 600,

          backgroundColor: colors.primary['25'],
          color: colors.primary['100'],

          '&:hover': {
            backgroundColor: colors.primary['50'],
            color: colors.primary['175'],
          },

          '&:disabled': {
            backgroundColor: colors.fog['80'],
            color: colors.neutral['100'],
          },
        },
      },
    ],
  },
};

export const theme = createTheme({
  palette: {
    ...colors,
  },
  typography: {
    fontFamily: ['Nunito Sans', 'sans-serif'].join(','),
    ...typography.text,
  },
  customShadows: {
    premium: '0px 4px 20px 5px rgba(0,0,0,0.04)',
    premiumHover: '0px 6px 28px 8px rgba(0,0,0,0.06)',
    premiumActive: '0px 3px 14px 4px rgba(0,0,0,0.06)',
    ring: '0 0 4px 1px rgba(0, 0, 0, 0.1)',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          borderRadius: 8,
          backgroundColor: colors.neutral['100'],

          '&:hover': {
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary['125'],
            },
            '.MuiInputAdornment-root': {
              color: colors.primary['125'],
            },
          },

          '&.Mui-focused': {
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary['100'],
            },
            '.MuiInputAdornment-root': {
              color: colors.primary['100'],
            },
          },

          '&.Mui-error': {
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: colors.red['100'],
            },
            '.MuiInputAdornment-root': {
              color: colors.red['100'],
            },

            '&.MuiInputBase-filledCustom': {
              backgroundColor: colors.red['10'],
            },
          },

          '&.Mui-disabled': {
            backgroundColor: colors.fog['90'],

            '.MuiOutlinedInput-notchedOutline': {
              borderColor: colors.fog['85'],
            },
            '.MuiInputAdornment-root': {
              color: colors.fog['80'],
            },
          },

          '&.MuiInputBase-filledCustom': {
            backgroundColor: colors.primary['10'],
          },

          // '&.MuiSelect-root': {
          //   padding: 0,
          // },
        },
        notchedOutline: {
          borderRadius: 8,
          borderColor: colors.fog['80'],
        },

        input: {
          padding: 0,
          color: colors.fog['10'],

          '&::placeholder': {
            opacity: 1,
            color: colors.fog['65'],
          },

          '&:disabled': {
            WebkitTextFillColor: colors.fog['80'],

            '&:not(:placeholder-shown)': {
              WebkitTextFillColor: colors.fog['40'],
            },
          },
        },
      },
      variants: [
        {
          props: {
            size: 'large',
          },
          style: {
            padding: '10px 12px',
          },
        },
      ],
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '0 0 0 12px',

          '& .MuiSelect-icon': {
            right: 8, // отступ справа для стрелки
          },
        },
        select: {
          '&&': {
            paddingRight: 32, // твое желаемое значение
          },

          padding: '8px 30px 8px 0',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: colors.fog['35'],
          '& .MuiSvgIcon-root': {
            fontSize: 18, // размер иконок
          },
        },
        positionStart: {
          marginRight: 8,
        },
        positionEnd: {
          marginLeft: 8,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginTop: 2,
          ...typography.text['body-caption'],

          '&.Mui-error': {
            color: colors.red['100'],
          },
        },
      },
    },

    ...refactoredButtons,

    // MuiButton: {
    //   defaultProps: {
    //     variant: 'contained',
    //     size: 'medium',
    //   },
    //   styleOverrides: {
    //     root: {
    //       textTransform: 'none',
    //       borderRadius: 8,
    //       boxShadow: 'none',
    //       ...typography.text['body-md'],
    //       fontWeight: 600,
    //       transition:
    //         'background-color 0.2s, color 0.2s, box-shadow 0.2s, border-color 0.2s',
    //       lineHeight: '24px',
    //     },
    //
    //     startIcon: {
    //       marginRight: 10,
    //       '& > *:nth-of-type(1)': {
    //         fontSize: 'unset',
    //       },
    //     },
    //     endIcon: {
    //       marginLeft: 10,
    //       '& > *:nth-of-type(1)': {
    //         fontSize: 'unset',
    //       },
    //     },
    //   },
    //   variants: [
    //     {
    //       props: { size: 'large' },
    //       style: {
    //         padding: '12px 24px',
    //       },
    //     },
    //     {
    //       props: { size: 'medium' },
    //       style: {
    //         padding: '10px 24px',
    //       },
    //     },
    //     {
    //       props: { size: 'small' },
    //       style: {
    //         padding: '6px 24px',
    //       },
    //     },
    //     {
    //       props: { size: 'mobile' },
    //       style: {
    //         padding: '8px 12px',
    //         // padding: '10px 12px',
    //         // ...typography.text['body-sm'],
    //         // fontWeight: 600,
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'contained',
    //         color: 'primary',
    //       },
    //       style: {
    //         backgroundColor: colors.primary[100],
    //         color: colors.neutral[100],
    //
    //         '&:hover': {
    //           backgroundColor: colors.primary[125],
    //           boxShadow: 'none',
    //         },
    //
    //         '&:disabled': {
    //           backgroundColor: colors.fog['80'],
    //           color: colors.neutral[100],
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'text',
    //       },
    //       style: {
    //         '&:hover': {
    //           backgroundColor: colors.primary[25],
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'link',
    //       },
    //       style: {
    //         padding: '10px 0',
    //         ...typography.text['body-md'],
    //         textDecoration: 'underline',
    //         textDecorationThickness: '1px',
    //         textDecorationColor: colors.primary[100],
    //
    //         color: colors.primary['100'],
    //
    //         '&:hover': {
    //           textDecoration: 'underline',
    //           textDecorationThickness: '1px',
    //           color: colors.primary['125'],
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'outlined',
    //         size: 'medium',
    //       },
    //       style: {
    //         padding: '9px 24px',
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'outlined',
    //         size: 'small',
    //       },
    //       style: {
    //         padding: '5px 24px',
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'outlined',
    //         size: 'mobile',
    //       },
    //       style: {
    //         padding: '10px 12px',
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'outlined',
    //         color: 'primary',
    //       },
    //       style: {
    //         color: colors.primary['100'],
    //         borderColor: colors.primary['100'],
    //
    //         '&:hover': {
    //           color: colors.primary['125'],
    //           borderColor: colors.primary['125'],
    //           backgroundColor: colors.primary['10'],
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'outlined',
    //         color: 'error',
    //       },
    //       style: {
    //         color: colors.red['100'],
    //         borderColor: colors.red['100'],
    //
    //         '&:hover': {
    //           color: colors.red['125'],
    //           borderColor: colors.red['125'],
    //           backgroundColor: colors.red['10'],
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: 'label',
    //       },
    //       style: {
    //         padding: '4px 12px',
    //         lineHeight: '16px',
    //         ...typography.text['body-sm'],
    //         fontWeight: 600,
    //
    //         backgroundColor: colors.primary['25'],
    //         color: colors.primary['100'],
    //
    //         '&:hover': {
    //           backgroundColor: colors.primary['50'],
    //           color: colors.primary['175'],
    //         },
    //
    //         '&:disabled': {
    //           backgroundColor: colors.fog['80'],
    //           color: colors.neutral['100'],
    //         },
    //       },
    //     },
    //   ],
    // },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 0,
          marginRight: 12,

          svg: {
            width: 20,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          // ...typography.text.body2,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: 'none',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // ...typography.text.body2,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          background: colors.neutral['100'],
          border: `1px solid ${colors.fog['80']}`,
          borderRadius: 8,

          '& .MuiInput-root .MuiInput-input': {
            padding: '0px 6px',
          },

          '& .MuiInput-root': {
            padding: '8px 12px',
            gap: '4px',
          },

          '& .MuiAutocomplete-endAdornment': {
            paddingRight: 4,
          },
        },

        popupIndicator: {
          '&[aria-expanded="true"]': {
            transform: 'none',
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: '1px solid rgba(16,24,40,.08)',
          boxShadow:
            '0 12px 30px rgba(16,24,40,.12), 0 2px 8px rgba(16,24,40,.06)',
        },
      },
    },
    MuiPopover: { defaultProps: { elevation: 0 } },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
      },
    },

    MuiTouchRipple: {
      styleOverrides: {
        child: ({ theme }) => ({
          backgroundColor: 'red',
        }),
      },
    },
  },
});
